import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getCookie, redirectToLogin } from '../utils/fetchWithAuth';
import { aggregateHeatmapItems } from '../../../utils/heatmap';
import { formatFenixTreeMapItems } from '../../../clients/fenixClient';
import {
    FENIX_INHERIT_COUNTRY_CODE,
    filterObjectsToPairs,
    replaceInheritedValueInFenixFilters,
} from '../../../components/HighCharts/utils';
import { configHasInheritCountryFilter } from '../utils/filterParams';
import { buildDataRequests } from '../../../chartRenderer';

const DEBOUNCE_MS = 300;
const SAMPLE_SIZE = 50;
const PAGE_SIZE = 1000;
const INITIAL_PAGES = 5;

export const usePreviewData = (config, chartBlock = null) => {
    const [previewData, setPreviewData] = useState(null);
    const [rowCount, setRowCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const debounceRef = useRef(null);
    const abortControllerRef = useRef(null);
    const cachedItemsRef = useRef({ items: [], config: null });

    // The refetch key is the renderer's own request list — serialising
    // buildDataRequests means every data-affecting field the renderer reads
    // (each series' dataset, filters, property, category, label…) is folded in
    // automatically, so adding a chart field can no longer silently break
    // preview refresh. It resolves the requests exactly as the fetch branches
    // below do, including the config-only fallback used before the config
    // exports cleanly, so the key and the fetch can never disagree about what
    // is being requested. Only the preview-owned knobs that change what is
    // fetched but never reach the chart block (sample vs full, the stand-in
    // inherit country) and the chart type (which selects the fetch/shaping
    // branch below) are added on top.
    const dataHash = useMemo(
        () =>
            JSON.stringify({
                requests: chartBlock
                    ? buildDataRequests(chartBlock)
                    : (config.series || []).map((series) => ({
                          dataset: series.dataSource?.value,
                          filterParams: series.filterParams || [],
                      })),
                chartType: config.chartType,
                previewMode: config.previewMode,
                inheritCountry: config.previewInheritCountry,
            }),
        [
            chartBlock,
            config.series,
            config.chartType,
            config.previewMode,
            config.previewInheritCountry,
        ]
    );

    // Helper to fetch data
    const fetchDataset = async (
        dataset,
        filterParams,
        signal,
        fetchAll = false
    ) => {
        // Editor state holds filters as [{field, op, value}]; convert to the
        // wire-shape pair list at this boundary so `op: 'ne'` becomes a
        // `field__ne` key. Empty values are dropped to avoid API errors.
        // "Inherit country code" placeholders resolve to the preview country
        // (mirroring how the published chart resolves them from the page's
        // country); without a preview country they're dropped — sent
        // literally they match zero rows and the preview goes blank.
        const filterPairs = filterParams?.every((filter) => filter?.key)
            ? filterParams
            : filterObjectsToPairs(filterParams);
        const tuples = replaceInheritedValueInFenixFilters(
            filterPairs,
            config.previewInheritCountry
        )
            .filter((p) => p.value !== '' && p.value != null)
            .filter((p) => p.value !== FENIX_INHERIT_COUNTRY_CODE)
            .map((p) => [p.key, String(p.value)]);
        const params = new URLSearchParams(tuples);
        if (fetchAll) {
            params.set('fetch_all', '1');
        } else {
            params.set('page_size', String(PAGE_SIZE));
            params.set('page', '1');
        }
        const url = `/wt/fenix/graphs/data/${dataset}/?${params.toString()}`;

        const response = await fetch(url, {
            headers: {
                'X-CSRFToken': getCookie('csrftoken'),
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            credentials: 'same-origin',
            signal,
            redirect: 'manual',
        });

        if (
            response.type === 'opaqueredirect' ||
            response.status === 401 ||
            response.status === 403
        ) {
            redirectToLogin();
            return null;
        }

        if (!response.ok) {
            const body = await response.json().catch(() => null);
            throw new Error(
                body?.detail || `Failed to fetch data: ${response.status}`
            );
        }

        return response.json();
    };

    const fetchData = useCallback(async () => {
        const firstSeries = config.series?.[0];
        const dataset = firstSeries?.dataSource?.value;

        // Check if we have minimum required config
        if (!config.chartType || !dataset) {
            setPreviewData(null);
            setRowCount(0);
            setTotalCount(0);
            return;
        }

        // Validate based on chart type
        if (config.chartType === 'timeline') {
            if (!config.timelineDateProperty || !config.timelineTitleProperty) {
                setPreviewData(null);
                setRowCount(0);
                setTotalCount(0);
                return;
            }
        } else if (config.chartType === 'treemap') {
            if (
                !firstSeries?.serie?.property ||
                !config.hierarchyLevels?.length
            ) {
                setPreviewData(null);
                setRowCount(0);
                setTotalCount(0);
                return;
            }
        } else if (config.chartType === 'heatmap') {
            if (
                !firstSeries?.serie?.property ||
                !firstSeries?.serie?.xAxisCategoryProperty ||
                !firstSeries?.serie?.yAxisCategoryProperty
            ) {
                setPreviewData(null);
                setRowCount(0);
                setTotalCount(0);
                return;
            }
        } else {
            // Standard charts need property and category
            if (
                !firstSeries?.serie?.property ||
                !firstSeries?.serie?.xAxisCategoryProperty
            ) {
                setPreviewData(null);
                setRowCount(0);
                setTotalCount(0);
                return;
            }
        }

        // Cancel any pending request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        setLoading(true);
        setError(null);

        try {
            if (config.chartType === 'treemap') {
                const requests = chartBlock
                    ? buildDataRequests(chartBlock)
                    : config.series.map((series) => ({
                          dataset: series.dataSource?.value,
                          filterParams: series.filterParams || [],
                      }));
                const results = await Promise.all(
                    requests.map(async ({ dataset, filterParams }) => {
                        const data = await fetchDataset(
                            dataset,
                            filterParams,
                            abortController.signal,
                            config.previewMode !== 'sampled'
                        );
                        if (!data) return { items: [], total: 0 };

                        let items = data.items || [];
                        if (config.previewMode === 'sampled') {
                            items = items.slice(0, SAMPLE_SIZE);
                        }
                        return {
                            items,
                            total: data.total || items.length,
                        };
                    })
                );

                cachedItemsRef.current = {
                    items: results[0]?.items || [],
                    config: JSON.stringify(config),
                };
                setTotalCount(
                    results.reduce((sum, result) => sum + result.total, 0)
                );

                // Reuse the shared data formatter so the renderer receives
                // the same hierarchy nodes as published charts.
                // the preview aggregates leaves, applies label properties, and
                // builds the same node IDs as the rendered chart. Hierarchy
                // levels come through as bare field strings when built fresh in
                // the editor, or as { property, labelProperty } objects when
                // imported from Wagtail; normalise both to the formatter's
                // ontologyProperty shape.
                const treemapLevels = (config.hierarchyLevels || []).map(
                    (level) =>
                        typeof level === 'string'
                            ? { ontologyProperty: level }
                            : {
                                  ontologyProperty:
                                      level.property || level.ontologyProperty,
                                  labelProperty: level.labelProperty,
                              }
                );
                const treemapData = results.map(({ items }, index) =>
                    formatFenixTreeMapItems(
                        items,
                        config.series[index]?.serie,
                        treemapLevels
                    )
                );

                setPreviewData({ rows: treemapData });
                setRowCount(
                    results.reduce(
                        (sum, result) => sum + result.items.length,
                        0
                    )
                );
                return;
            }

            if (config.chartType === 'timeline') {
                const filterParams = firstSeries.filterParams || [];
                const data = await fetchDataset(
                    dataset,
                    filterParams,
                    abortController.signal,
                    config.previewMode !== 'sampled'
                );
                if (!data) return;

                let items = data.items || [];
                const total = data.total || items.length;
                if (config.previewMode === 'sampled') {
                    items = items.slice(0, SAMPLE_SIZE);
                }

                cachedItemsRef.current = {
                    items,
                    config: JSON.stringify(config),
                };
                setTotalCount(total);

                // Rows stay unshaped here; sorting, spacing, label formatting
                // and option assembly belong to the shared renderer.
                setPreviewData({ rows: [items] });
                setRowCount(items.length);
                return;
            }

            if (config.chartType === 'heatmap') {
                // Heatmap is single-series; errors bubble to outer catch.
                const filterParams = firstSeries.filterParams || [];
                const data = await fetchDataset(
                    dataset,
                    filterParams,
                    abortController.signal,
                    config.previewMode !== 'sampled'
                );
                if (!data) return;

                let items = data.items || [];
                const total = data.total || items.length;
                if (config.previewMode === 'sampled') {
                    items = items.slice(0, SAMPLE_SIZE);
                }

                cachedItemsRef.current = {
                    items,
                    config: JSON.stringify(config),
                };
                setTotalCount(total);

                // Rows stay unshaped here; ordering, label formatting and
                // option assembly belong to the shared renderer.
                const aggregated = aggregateHeatmapItems(items, {
                    property: firstSeries.serie?.property,
                    xAxisCategoryProperty:
                        firstSeries.serie?.xAxisCategoryProperty,
                    yAxisCategoryProperty:
                        firstSeries.serie?.yAxisCategoryProperty,
                });

                setPreviewData({ rows: [aggregated] });
                setRowCount(items.length);
                return;
            }

            // Standard charts fetch the requests emitted by the shared
            // renderer. The rows stay unshaped here; option assembly,
            // aggregation, sorting and axis merging belong to that renderer.
            const requests = chartBlock
                ? buildDataRequests(chartBlock)
                : config.series.map((series) => ({
                      dataset: series.dataSource?.value || dataset,
                      filterParams: series.filterParams || [],
                  }));
            if (!requests.length) {
                setPreviewData(null);
                setRowCount(0);
                setTotalCount(0);
                return;
            }
            const allSeriesResults = await Promise.all(
                requests.map(
                    async ({ dataset: seriesDataset, filterParams }) => {
                        try {
                            const data = await fetchDataset(
                                seriesDataset,
                                filterParams,
                                abortController.signal,
                                config.previewMode !== 'sampled'
                            );
                            if (!data) return { items: [], total: 0 };
                            let seriesItems = data.items || [];
                            const total = data.total || seriesItems.length;
                            if (config.previewMode === 'sampled') {
                                seriesItems = seriesItems.slice(0, SAMPLE_SIZE);
                            }
                            return { items: seriesItems, total };
                        } catch (err) {
                            if (err.name === 'AbortError') throw err;
                            console.error('Failed to fetch series:', err);
                            return { items: [], total: 0 };
                        }
                    }
                )
            );

            const allSeriesItems = allSeriesResults.map((r) => r.items);
            const firstResult = allSeriesResults[0];

            cachedItemsRef.current = {
                items: firstResult.items,
                config: JSON.stringify(config),
            };
            setTotalCount(firstResult.total);

            setPreviewData({ rows: allSeriesItems });
            const totalRows = allSeriesItems.reduce(
                (sum, items) => sum + items.length,
                0
            );
            setRowCount(totalRows);
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error('Error fetching preview data:', err);
            setError(err.message);
            setPreviewData(null);
        } finally {
            setLoading(false);
        }
    }, [dataHash]);

    // Debounced fetch on config change
    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            fetchData();
        }, DEBOUNCE_MS);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [fetchData]);

    const retry = useCallback(() => {
        fetchData();
    }, [fetchData]);

    // Drives the preview's "Inherited country" picker: these filters only
    // resolve on a published country page, so the preview lets the user pick
    // a stand-in country (or shows all countries when none is picked).
    const hasInheritCountryFilter = useMemo(
        () => configHasInheritCountryFilter(config),
        [config.series]
    );

    return {
        previewData,
        rowCount,
        totalCount,
        loading,
        error,
        retry,
        hasInheritCountryFilter,
    };
};
